import type { ReactNode } from "react";
import type { PageNode } from "../../../shared/src/pages";
import { pageUpdateDate, type PageUpdate } from "../../../shared/src/page-updates-runtime";
import { PostActions, type PostActionsPost } from "./post-actions";
import { PostCover } from "./post-feed-card";

/**
 * A post as a social card: the title leads, a locked post shows its authored teaser and a
 * blurred stand-in for the rest, and a row of likes, comments, sharing and gifting closes it. The
 * blurred lines are fixed copy, never the post's own words. A page feed (Post style → Social)
 * and the posts page (Post cards → Social) both draw it.
 */
export function SocialPostCard({
  post,
  href,
  date,
  text,
  cover,
  unlockHref,
  unlockLabel = "Join to unlock",
  giftHref,
  editHref,
  heading: Heading = "h3",
  density,
  children,
}: {
  post: PostActionsPost;
  href: string | null;
  date: { dateTime: string; label: string } | null;
  /** What a reader sees of the post: a locked post's authored summary only. */
  text: string;
  cover?: ReactNode;
  unlockHref: string;
  unlockLabel?: string;
  giftHref: string;
  editHref?: string;
  heading?: "h2" | "h3";
  density?: "comfortable" | "compact";
  /** Under the text: a post's tags, for instance. */
  children?: ReactNode;
}) {
  return (
    <article
      className="drate-post-card"
      data-post-style="social"
      data-update-id={post.id}
      data-locked={post.locked}
      data-example={post.example ? "" : undefined}
      data-join={unlockHref}
      data-post-density={density}
    >
      {cover}
      <div className="drate-post-content">
        <Heading className="drate-post-title">
          {href ? <a href={href}>{post.title}</a> : post.title}
        </Heading>
        {date && (
          <time className="drate-post-date" dateTime={date.dateTime}>
            {date.label}
          </time>
        )}
        {text && <p className="drate-post-summary">{text}</p>}
        {post.locked && (
          <>
            <p className="drate-post-teaser" aria-hidden="true">
              Become a member to read the rest of this post, and everything else shared just for
              members.
            </p>
            <a className="button button-outline drate-post-unlock" href={unlockHref}>
              {unlockLabel}
            </a>
          </>
        )}
        {children}
      </div>
      <PostActions
        post={post}
        href={href}
        commentsHref={href ? `${href}#comments` : unlockHref}
        giftHref={giftHref}
        editHref={editHref}
      />
    </article>
  );
}

/** A page feed's post as a social card (Post style → Social). */
export function PostSocialCard({
  item,
  node,
  editHref,
  linkTo = (href) => href,
  canvas = false,
}: {
  item: PageUpdate;
  node: Extract<PageNode, { kind: "updates" }>;
  editHref?: string;
  linkTo?: (href: string) => string;
  canvas?: boolean;
}) {
  const href = item.url ? linkTo(item.url) : null;
  return (
    <SocialPostCard
      post={item}
      href={href}
      date={
        node.dateFormat !== "hidden" && item.date
          ? { dateTime: item.date, label: pageUpdateDate(item.date, node) }
          : null
      }
      text={item.locked ? item.summary || "" : item.summary || item.body}
      cover={<PostCover item={item} href={href ?? linkTo("/join")} canvas={canvas} />}
      unlockHref={linkTo("/join")}
      giftHref={linkTo("/join/gift")}
      editHref={editHref}
      density={node.postDensity ?? "comfortable"}
    />
  );
}
